import { ChartConfig, ChartContainer, ChartTooltip } from '@alphabait/ui' // chart'
import { BarChart, Bar, XAxis, YAxis, LabelList, Cell } from 'recharts'
import { useTranslation } from 'react-i18next'
import { BaseQuestionRendererProps } from './types'
import { ChartWrapper, ChartEmptyState } from './shared'

export function PictureChoiceBars({ question }: BaseQuestionRendererProps) {
  const { t } = useTranslation()
  const buckets = question.aggregates?.buckets || []
  const totalResponses = question.totalRespondents || 0
  const options = question.options || []

  // Create chart data from aggregated buckets (pre-calculated in backend)
  // Match by label since answer_text stores labels, not option IDs
  const completeChartData = buckets.map((bucket) => {
    // Find matching option by label (primary) or value (fallback for old data)
    const option = options.find(
      (o) =>
        o.label === bucket.label ||
        o.label === bucket.value ||
        o.value === bucket.value,
    )
    return {
      label: bucket.label || bucket.value || '',
      value: bucket.value || '',
      count: bucket.count,
      percentage: bucket.percentage,
      image: option?.image || bucket.image,
    }
  })

  // Add options with 0 responses that aren't in buckets
  options.forEach((option) => {
    const alreadyIncluded = completeChartData.some(
      (item) => item.label === option.label,
    )
    if (!alreadyIncluded) {
      completeChartData.push({
        label: option.label,
        value: option.value || '',
        count: 0,
        percentage: 0,
        image: option.image,
      })
    }
  })

  // Sort by count descending, but keep 0-count items at the end in their original order
  const withResponses = completeChartData
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count)
  const withoutResponses = completeChartData.filter((item) => item.count === 0)
  const sortedData = [...withResponses, ...withoutResponses]

  // Collapse categories if more than 8
  let chartData = sortedData.slice()
  if (sortedData.length > 8) {
    const topBuckets = sortedData.slice(0, 7)
    const otherCount = sortedData
      .slice(7)
      .reduce((sum, bucket) => sum + bucket.count, 0)
    const otherPercentage =
      topBuckets.length > 0
        ? Math.round(
            (otherCount /
              (topBuckets.reduce((sum, b) => sum + b.count, 0) + otherCount)) *
              1000,
          ) / 10
        : 0
    chartData = [
      ...topBuckets,
      {
        label: t('dashboard.charts.other'),
        value: 'other',
        count: otherCount,
        percentage: otherPercentage,
        image: undefined,
      },
    ]
  }

  // Custom colors for picture choice bars
  const pictureChoiceColors = ['#72DD68', '#BEE6D2', '#E8F3EE']

  // Custom label renderer for labels inside bars
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderBarLabel = (props: any) => {
    const { x, y, height, value, index } = props
    const dataItem = chartDataWithColors[index]
    if (!dataItem) return null

    const label = dataItem.label
    const maxChars = 40
    const display =
      label.length > maxChars ? label.slice(0, maxChars - 1) + '…' : label

    // For zero values, show label at the start with lighter color
    if (value === 0) {
      return (
        <text
          x={x + 12}
          y={y + height / 2}
          fill="#737373"
          textAnchor="start"
          dominantBaseline="middle"
          fontSize={12}
        >
          {display}
        </text>
      )
    }

    // For non-zero values, show label inside the bar
    return (
      <text
        x={x + 12}
        y={y + height / 2}
        fill="#0A0A0A"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={12}
      >
        {display}
      </text>
    )
  }

  // Calculate max count for consistent label positioning
  const maxCount = Math.max(...chartData.map((d) => d.count), 1)

  // Custom count label renderer to hide "0" values
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCountLabel = (props: any) => {
    const { x, y, width, height, value } = props

    // Don't show count for zero values
    if (value === 0) {
      return null
    }

    // Calculate where the max bar would end, then position count there
    // width/value gives us the scale factor (pixels per unit)
    const scaleFactor = width / value
    const maxBarWidth = scaleFactor * maxCount

    return (
      <text
        x={x + maxBarWidth + 8}
        y={y + height / 2}
        fill="#737373"
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={12}
      >
        {value}
      </text>
    )
  }

  // Assign a color to each bar, cycling through the 3 colors
  const chartDataWithColors = chartData.map((item, index) => ({
    ...item,
    fill: pictureChoiceColors[index % pictureChoiceColors.length],
  }))

  const chartConfig: ChartConfig = {
    count: {
      label: t('dashboard.charts.count'),
      color: pictureChoiceColors[0],
    },
  }

  if (options.length === 0 && buckets.length === 0) {
    return (
      <ChartWrapper
        title={question.label}
        description={t('dashboard.charts.picture_choice_distribution')}
      >
        <ChartEmptyState message={t('dashboard.charts.no_responses_yet')} />
      </ChartWrapper>
    )
  }

  return (
    <ChartWrapper
      title={question.label}
      description={`${t('dashboard.charts.picture_choice_distribution')} (${t(
        'dashboard.charts.people_answered',
        { count: totalResponses },
      )})`}
      className="flex flex-col p-0"
      contentClassName="items-start"
    >
      <ChartContainer
        config={chartConfig}
        style={{ height: `${Math.max(150, chartData.length * 50)}px` }}
      >
        <BarChart
          data={chartDataWithColors}
          layout="vertical"
          margin={{ top: 10, right: 50, bottom: 10 }}
          barCategoryGap="10%"
        >
          <YAxis orientation="left" dataKey="label" type="category" hide />
          <XAxis type="number" dataKey="count" hide />
          <ChartTooltip
            cursor={false}
            content={({ active, payload }) => {
              if (active && payload && payload.length > 0) {
                const data = payload[0].payload
                return (
                  <div className="bg-white p-3 border rounded-lg shadow-lg">
                    <p className="font-medium text-sm mb-1">{data.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('dashboard.charts.count')}: {data.count}
                    </p>
                  </div>
                )
              }
              return null
            }}
          />
          <Bar dataKey="count" radius={100} barSize={35}>
            {chartDataWithColors.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
            <LabelList
              dataKey="count"
              position="insideLeft"
              content={renderBarLabel}
            />
            <LabelList
              dataKey="count"
              position="right"
              content={renderCountLabel}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </ChartWrapper>
  )
}
